import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

/**
 * KDS theme runtime.
 *
 * `mode` is what the visitor chose: 'system' | 'light' | 'dark'.
 * `theme` is what is actually painted: 'light' | 'dark'.
 *
 * The resolved theme is written to <html data-theme> so every token in
 * design-system/tokens.css switches at once. public/index.html runs the same
 * resolution inline before first paint, so there is no flash of the wrong
 * scheme; this provider only keeps it in sync afterwards.
 */

export const THEME_STORAGE_KEY = 'kds-theme';
export const THEME_MODES = ['system', 'light', 'dark'];

const ThemeContext = createContext(null);

function prefersDark() {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false;
  try {
    return window.matchMedia('(prefers-color-scheme: dark)').matches;
  } catch (error) {
    return false;
  }
}

export function readStoredMode() {
  if (typeof window === 'undefined') return 'system';
  try {
    const stored = window.localStorage.getItem(THEME_STORAGE_KEY);
    return THEME_MODES.includes(stored) ? stored : 'system';
  } catch (error) {
    // Private browsing or blocked storage: fall back to following the OS.
    return 'system';
  }
}

export function resolveTheme(mode) {
  if (mode === 'light' || mode === 'dark') return mode;
  return prefersDark() ? 'dark' : 'light';
}

function applyTheme(theme) {
  if (typeof document === 'undefined') return;
  const root = document.documentElement;
  root.dataset.theme = theme;

  // Keep the browser/OS chrome in step with the page surface.
  const meta = document.querySelector('meta[name="theme-color"]');
  if (meta) {
    const surface = getComputedStyle(root).getPropertyValue('--kds-surface').trim();
    if (surface) meta.setAttribute('content', surface);
  }
}

export function ThemeProvider({ children }) {
  const [mode, setModeState] = useState(readStoredMode);
  const [theme, setTheme] = useState(() => resolveTheme(readStoredMode()));

  useEffect(() => {
    applyTheme(theme);
  }, [theme]);

  useEffect(() => {
    setTheme(resolveTheme(mode));
    if (mode !== 'system') return undefined;
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return undefined;

    const query = window.matchMedia('(prefers-color-scheme: dark)');
    const onChange = (event) => setTheme(event.matches ? 'dark' : 'light');
    // Safari < 14 only has the deprecated listener API.
    if (typeof query.addEventListener === 'function') {
      query.addEventListener('change', onChange);
      return () => query.removeEventListener('change', onChange);
    }
    query.addListener(onChange);
    return () => query.removeListener(onChange);
  }, [mode]);

  const setMode = useCallback((nextMode) => {
    const safeMode = THEME_MODES.includes(nextMode) ? nextMode : 'system';
    setModeState(safeMode);
    try {
      if (safeMode === 'system') {
        window.localStorage.removeItem(THEME_STORAGE_KEY);
      } else {
        window.localStorage.setItem(THEME_STORAGE_KEY, safeMode);
      }
    } catch (error) {
      // Preference is not persistable; the in-memory choice still applies.
    }
  }, []);

  const cycleMode = useCallback(() => {
    setMode(THEME_MODES[(THEME_MODES.indexOf(mode) + 1) % THEME_MODES.length]);
  }, [mode, setMode]);

  const value = useMemo(() => ({ mode, theme, setMode, cycleMode }), [mode, theme, setMode, cycleMode]);

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme() {
  const context = useContext(ThemeContext);
  if (context) return context;
  // Rendering a primitive outside the provider must never crash a page.
  return { mode: 'system', theme: 'light', setMode: () => {}, cycleMode: () => {} };
}

export default ThemeProvider;
